import Navbar from "@/components/Layout/Navbar";
import Footer from "@/components/Layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Globe, Users, Code, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/SEOHead";
import { PAGE_SEO_CONFIG } from "@/lib/seo";
import WhyAfricaSection from "@/components/SEO/WhyAfricaSection";
import FAQSection from "@/components/SEO/FAQSection";
import RelatedLinks from "@/components/SEO/RelatedLinks";
import TrustBar from "@/components/EEAT/TrustBar";
import ArticleMeta from "@/components/EEAT/ArticleMeta";
import AuthorBio from "@/components/EEAT/AuthorBio";
import { WHY_NEARSHORE_FAQS_DE, WHY_NEARSHORE_FAQS_EN } from "@/lib/seoFaqs";

const WhatIsNearshoring = () => {
  const { t, i18n } = useTranslation('whatIsNearshoring');
  const seoConfig = PAGE_SEO_CONFIG.whatIsNearshoring;
  const isDe = i18n.language === 'de';
  const faqs = isDe ? WHY_NEARSHORE_FAQS_DE : WHY_NEARSHORE_FAQS_EN;

  const features = [
    { icon: Globe, title: t('keyFeatures.location.title'), description: t('keyFeatures.location.description') },
    { icon: Users, title: t('keyFeatures.talent.title'), description: t('keyFeatures.talent.description') },
    { icon: Code, title: t('keyFeatures.cost.title'), description: t('keyFeatures.cost.description') },
    { icon: TrendingUp, title: t('keyFeatures.growth.title'), description: t('keyFeatures.growth.description') },
  ];

  const reasons = [
    { title: t('whyChoose.timezone.title'), description: t('whyChoose.timezone.description') },
    { title: t('whyChoose.culture.title'), description: t('whyChoose.culture.description') },
    { title: t('whyChoose.education.title'), description: t('whyChoose.education.description') },
    { title: t('whyChoose.scalability.title'), description: t('whyChoose.scalability.description') },
    { title: t('whyChoose.innovation.title'), description: t('whyChoose.innovation.description') },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        titleEn={seoConfig.titleEn}
        titleDe={seoConfig.titleDe}
        descriptionEn={seoConfig.descriptionEn}
        descriptionDe={seoConfig.descriptionDe}
        keywords={seoConfig.keywords}
        path={seoConfig.path}
        faqs={faqs}
        breadcrumbs={[
          { name: 'Home', nameDE: 'Startseite', url: '/' },
          { name: 'What is Nearshoring?', nameDE: 'Was ist Nearshoring?', url: '/what-is-nearshoring' }
        ]}
      />
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="py-32">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
                {isDe ? "Leitfaden" : "Guide"}
              </div>
              <h1 className="text-5xl md:text-6xl font-semibold tracking-[-0.035em] text-balance mb-6">
                {isDe
                  ? "Warum Nearshore-Softwareentwicklung aus Afrika?"
                  : "Why Nearshore Software Development from Africa?"}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {isDe
                  ? "Der vollständige Leitfaden für deutsche Unternehmen: Vorteile, Kosten, DSGVO und Vergleich Afrika vs. Osteuropa für IT-Outsourcing 2026."
                  : "The complete guide for European companies: benefits, costs, GDPR and Africa vs. Eastern Europe comparison for IT outsourcing in 2026."}
              </p>
              <div className="mt-8 flex justify-center">
                <ArticleMeta
                  publishedDate="2025-09-01"
                  updatedDate="2026-04-19"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Trust */}
        <TrustBar />

        {/* Definition */}
        <section className="py-32">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <Card>
                <CardContent className="p-10 md:p-14">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">
                    {isDe ? "Definition" : "Definition"}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-semibold tracking-[-0.028em] mb-6">{t('definition.title')}</h2>
                  <div className="space-y-5 text-foreground/80 leading-relaxed">
                    <p>{t('definition.paragraph1')}</p>
                    <p>{t('definition.paragraph2')}</p>
                    <p>{t('definition.paragraph3')}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-32 border-t border-border/60">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">
                  {isDe ? "Pfeiler" : "Pillars"}
                </div>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.028em] text-balance">
                  {t('keyFeatures.title')}
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {features.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <Card key={idx}>
                      <CardContent className="p-8">
                        <Icon className="w-5 h-5 text-muted-foreground mb-5" aria-hidden="true" />
                        <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3">{feature.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose */}
        <section className="py-32 border-t border-border/60">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-16">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">
                  {isDe ? "Gründe" : "Reasons"}
                </div>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.028em] text-balance">
                  {t('whyChoose.title')}
                </h2>
              </div>
              <div className="space-y-3">
                {reasons.map((reason, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-7">
                      <div className="flex items-start gap-5">
                        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium pt-1 min-w-[1.5rem]">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <div>
                          <h3 className="text-lg font-semibold tracking-[-0.02em] mb-2">{reason.title}</h3>
                          <p className="text-muted-foreground leading-relaxed">{reason.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Author */}
              <AuthorBio
                authorId="sherif-adel"
                reviewedByDe="Evolvice Editorial Team"
                reviewedByEn="Evolvice Editorial Team"
              />
            </div>
          </div>
        </section>

        <WhyAfricaSection />

        <FAQSection
          title={isDe ? "FAQ — Warum Nearshore-Softwareentwicklung aus Afrika?" : "FAQ — Why Nearshore Software Development from Africa?"}
          intro={isDe ? "Die wichtigsten Fragen zu Nearshore-Softwareentwicklung in Afrika für deutsche Unternehmen." : "The key questions on nearshore software development in Africa for European companies."}
          items={faqs}
        />

        <RelatedLinks />

        {/* CTA */}
        <section className="py-32 border-t border-border/60">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.028em] text-balance mb-6">
                {t('cta.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {t('cta.subtitle')}
              </p>
              <Button size="lg" asChild>
                <a href="https://www.evolvice.de/en" target="_blank" rel="noopener noreferrer">
                  {t('cta.button')}
                  <ArrowRight size={16} />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default WhatIsNearshoring;
